import React, {useEffect, useRef, useState} from 'react'
import {Group, Image as KonvaImage, Rect, Transformer, Line} from 'react-konva'
import useImage from 'use-image'
import {actionSaveCropLocal} from '../context/SelectedVariantContext/action'

// Remove fixed MIN_SIZE constant and add dynamic calculation
const BASE_HANDLE_LEN = 25
const BASE_HANDLE_HIT = 28
const BASE_STROKE_WIDTH = 6

// Function to calculate dynamic minimum size based on image dimensions
const calculateMinSize = (imageWidth, imageHeight) => {
    // Set minimum size as 15% of the smaller image dimension
    const smallerDimension = Math.min(imageWidth, imageHeight)
    return Math.max(50, Math.round(smallerDimension * 0.2))
}

// Function to calculate dynamic handle size based on 1/8 of image shorter dimension
const calculateHandleSize = (imageWidth, imageHeight) => {
    // Calculate handle length as 1/10 of the shorter dimension of the original image
    const shorterImageDimension = Math.min(imageWidth, imageHeight)
    const handleLen = Math.round(shorterImageDimension / 12)

    // Ensure minimum bounds only (no maximum limit)
    const finalHandleLen = Math.max(15, handleLen)

    // Calculate stroke width as 1/3 of handle length (thinner for smaller images)
    let strokeWidth = Math.max(4, Math.min(25, Math.round(finalHandleLen / 3)))

    // Additional boost for very small handles to ensure visibility
    if (finalHandleLen < 25) {
        strokeWidth = Math.round(strokeWidth * 1.3) // 30% boost for small handles (reduced from 50%)
    }

    // Additional boost for high resolution images (2K, 4K)
    if (finalHandleLen > 100) {
        strokeWidth = Math.round(strokeWidth * 1.3) // 30% boost for high res
    }
    if (finalHandleLen > 200) {
        strokeWidth = Math.round(strokeWidth * 1.5) // 50% boost for 4K (increased from 20%)
    }
    if (finalHandleLen > 300) {
        strokeWidth = Math.round(strokeWidth * 1.3) // Additional 30% boost for ultra high res
    }

    // Ensure stroke width is within reasonable bounds
    strokeWidth = Math.max(6, Math.min(60, strokeWidth))

    // Calculate hit area size (larger for better usability)
    const handleHit = Math.max(BASE_HANDLE_HIT, finalHandleLen * 1.5)

    return {
        handleLen: Math.round(finalHandleLen),
        strokeWidth: Math.round(strokeWidth),
        handleHit: Math.round(handleHit),
    }
}

export default function CropSelectorWithBounds({selectedLayer, dispatch}) {
    const [image] = useImage(selectedLayer.src)
    // Refs
    const overlayLayerRef = useRef(null)

    const imageRef = useRef(null)
    const trImageRef = useRef(null)

    const cropRectRef = useRef(null)
    const trCropRef = useRef(null)

    // State
    const [mode, setMode] = useState('edit') // "edit" | "crop"
    const [imgEl, setImgEl] = useState(null)

    // Displayed image node placement (unrotated width/height)
    const [fitBox, setFitBox] = useState({x: 0, y: 0, w: 0, h: 0})

    // Crop rect in IMAGE-LOCAL coordinates
    const [cropLocal, setCropLocal] = useState({
        x: selectedLayer.x,
        y: selectedLayer.y,
        w: selectedLayer.width,
        h: selectedLayer.height,
    })

    // Snapshot of image transform when entering crop mode
    const [imgSnap, setImgSnap] = useState(null) // { x,y,width,height,scaleX,scaleY,rotation }

    // After Apply: place new image at the same on-screen position/size
    const [pendingPlacement, setPendingPlacement] = useState(null) // { x, y, w, h } in Stage-space
    // After Apply: re-apply previous rotation to the new image node
    const [pendingRotation, setPendingRotation] = useState(null) // number | null

    // Dynamic handle size state
    const [handleSize, setHandleSize] = useState({
        handleLen: BASE_HANDLE_LEN,
        strokeWidth: BASE_STROKE_WIDTH,
        handleHit: BASE_HANDLE_HIT,
    })

    // Dynamic minimum size state based on image dimensions
    const [minSize, setMinSize] = useState(50)

    // Preview canvas
    const previewCanvasRef = useRef(null)

    // ---------------------- Load image when fileUrl changes ----------------------
    useEffect(() => {
        if (!selectedLayer.src) return
        const img = new window.Image()
        img.onload = () => {
            setImgEl(img)

            if (pendingPlacement) {
                // Place the new image exactly where the crop was seen
                const {x, y, w, h} = pendingPlacement
                setFitBox({
                    x: Math.round(x),
                    y: Math.round(y),
                    w: Math.max(1, Math.round(w)),
                    h: Math.max(1, Math.round(h)),
                })
                setPendingPlacement(null)
                setMode('edit')
                return
            }

            // Default fit-to-stage
            const ratio = Math.min(selectedLayer.rect.width / img.width, selectedLayer.rect.height / img.height)
            const w = Math.max(1, Math.floor(img.width * ratio))
            const h = Math.max(1, Math.floor(img.height * ratio))
            const x = Math.floor((selectedLayer.rect.width - w) / 2)
            const y = Math.floor((selectedLayer.rect.height - h) / 2)
            setFitBox({x, y, w, h})
            setMode('edit')

            // Force enter crop mode after image loads
            setTimeout(() => {
                if (imageRef.current) {
                    enterCropMode()
                }
            }, 200)
        }
        img.src = selectedLayer.src
        return () => setImgEl(null)
    }, [selectedLayer.src, pendingPlacement])

    // Re-apply previous rotation (if any) once the node is available in EDIT mode
    useEffect(() => {
        if (!imageRef.current) return

        // 1) Apply pending rotation first (before attaching Transformer)
        if (pendingRotation != null) {
            imageRef.current.rotation(pendingRotation)
        }

        // 2) Attach transformer to the image node
        if (trImageRef.current) {
            trImageRef.current.nodes([imageRef.current])
            trImageRef.current.getLayer()?.batchDraw()
        }

        // 3) Clear pending rotation after it is applied & drawn
        if (pendingRotation != null) {
            setPendingRotation(null)
        }
    }, [mode, imgEl, pendingRotation])

    // ---------------------- Enter crop mode ----------------------
    const enterCropMode = () => {
        const imgNode = imageRef.current
        if (!imgNode) {
            return
        }

        const snap = {
            x: imgNode.x(),
            y: imgNode.y(),
            width: imgNode.width() * (imgNode.scaleX() || 1),
            height: imgNode.height() * (imgNode.scaleY() || 1),
            scaleX: imgNode.scaleX() || 1,
            scaleY: imgNode.scaleY() || 1,
            rotation: imgNode.rotation() || 0,
        }
        setImgSnap(snap)

        // Full image crop (image-local) - cover entire image
        const newCropLocal = {x: 0, y: 0, w: snap.width, h: snap.height}
        setCropLocal(newCropLocal)

        // Calculate dynamic minimum size based on displayed image dimensions
        // This ensures consistency with clampLocal which uses imgSnap dimensions
        const dynamicMinSize = calculateMinSize(snap.width, snap.height)
        setMinSize(dynamicMinSize)

        const dynamicHandleSize = calculateHandleSize(selectedLayer.width, selectedLayer.height)

        setHandleSize(dynamicHandleSize)

        // Detach image transformer while cropping
        trImageRef.current?.nodes([])

        setMode('crop')
    }

    useEffect(() => {
        if (image && selectedLayer.src) {
            enterCropMode()
        }
    }, [image, selectedLayer.src])

    // ---------------------- Clamp crop in image-local space ----------------------
    const clampLocal = (next) => {
        if (!imgSnap) return next
        const bw = imgSnap.width
        const bh = imgSnap.height
        let {x, y, w, h} = next

        // Enforce minimum size constraints
        w = Math.max(minSize, Math.min(w, bw))
        h = Math.max(minSize, Math.min(h, bh))

        // Ensure position doesn't exceed bounds while maintaining minimum size
        const maxX = bw - w
        const maxY = bh - h
        x = Math.min(Math.max(x, 0), maxX)
        y = Math.min(Math.max(y, 0), maxY)

        return {x, y, w, h}
    }

    // ---------------------- Corner & Edge handle helpers ----------------------
    // Edge: type = 'tm', 'rm', 'bm', 'lm' (top, right, bottom, left middle)

    const onEdgeDragMove = (type) => (e) => {
        if (!imgSnap || !cropLocal) return

        const currentX = e.target.x()
        const currentY = e.target.y()

        // Calculate new crop dimensions based on edge type
        let newCrop = {...cropLocal}

        switch (type) {
            case 'tm': // Top-Middle
                // Clamp top edge to 0
                let newYTM = Math.max(0, Math.min(currentY, cropLocal.y + cropLocal.h - minSize))
                let newHTM = Math.max(minSize, cropLocal.y + cropLocal.h - newYTM)
                newCrop = {
                    x: cropLocal.x,
                    y: newYTM,
                    w: cropLocal.w,
                    h: newHTM,
                }
                break
            case 'bm': // Bottom-Middle
                // Clamp bottom edge to image height
                const maxBottomBM = imgSnap ? imgSnap.height : selectedLayer.height
                let bottomBM = Math.min(currentY, maxBottomBM)
                let newHBM = Math.max(minSize, bottomBM - cropLocal.y)
                newCrop = {
                    x: cropLocal.x,
                    y: cropLocal.y,
                    w: cropLocal.w,
                    h: newHBM,
                }
                break
            case 'lm': // Left-Middle
                // Clamp left edge to 0
                let newXLM = Math.max(0, Math.min(currentX, cropLocal.x + cropLocal.w - minSize))
                let newWLM = Math.max(minSize, cropLocal.x + cropLocal.w - newXLM)
                newCrop = {
                    x: newXLM,
                    y: cropLocal.y,
                    w: newWLM,
                    h: cropLocal.h,
                }
                break
            case 'rm': // Right-Middle
                // Clamp right edge to image width
                const maxRightRM = imgSnap ? imgSnap.width : selectedLayer.width
                let rightRM = Math.min(currentX, maxRightRM)
                let newWRM = Math.max(minSize, rightRM - cropLocal.x)
                newCrop = {
                    x: cropLocal.x,
                    y: cropLocal.y,
                    w: newWRM,
                    h: cropLocal.h,
                }
                break
            default:
                return
        }

        // Apply constraints using clampLocal
        const constrainedCrop = clampLocal(newCrop)

        // Update the edge handle position to match the constrained crop
        let handleX = constrainedCrop.x + constrainedCrop.w / 2
        let handleY = constrainedCrop.y + constrainedCrop.h / 2

        switch (type) {
            case 'tm':
                handleX = constrainedCrop.x + constrainedCrop.w / 2
                handleY = constrainedCrop.y
                break
            case 'bm':
                handleX = constrainedCrop.x + constrainedCrop.w / 2
                handleY = constrainedCrop.y + constrainedCrop.h
                break
            case 'lm':
                handleX = constrainedCrop.x
                handleY = constrainedCrop.y + constrainedCrop.h / 2
                break
            case 'rm':
                handleX = constrainedCrop.x + constrainedCrop.w
                handleY = constrainedCrop.y + constrainedCrop.h / 2
                break
            default:
                break
        }

        e.target.position({x: handleX, y: handleY})

        // Update crop rectangle
        if (cropRectRef.current) {
            const node = cropRectRef.current
            node.x(constrainedCrop.x)
            node.y(constrainedCrop.y)
            node.width(constrainedCrop.w)
            node.height(constrainedCrop.h)
        }

        setCropLocal(constrainedCrop)
        overlayLayerRef.current?.batchDraw?.()
    }

    const onCornerDragMove = (type) => (e) => {
        if (!imgSnap || !cropLocal) return

        const currentX = e.target.x()
        const currentY = e.target.y()

        // Calculate new crop dimensions based on corner type
        let newCrop = {...cropLocal}

        switch (type) {
            case 'tl': // Top-Left
                newCrop = {
                    x: currentX,
                    y: currentY,
                    w: currentX >= 0 ? cropLocal.x + cropLocal.w - currentX : newCrop.w,
                    h: currentY >= 0 ? cropLocal.y + cropLocal.h - currentY : newCrop.h,
                }
                break
            case 'tr': // Top-Right
                // Clamp right edge to image width
                const maxRight = imgSnap ? imgSnap.width : selectedLayer.width
                let right = Math.min(currentX, maxRight)
                let newW = Math.max(minSize, right - cropLocal.x)
                newCrop = {
                    x: cropLocal.x,
                    y: currentY,
                    w: newW,
                    h: currentY >= 0 ? cropLocal.y + cropLocal.h - currentY : newCrop.h,
                }
                break
            case 'br': // Bottom-Right
                // Clamp right edge to image width
                const maxRightBR = imgSnap ? imgSnap.width : selectedLayer.width
                let rightBR = Math.min(currentX, maxRightBR)
                let newWBR = Math.max(minSize, rightBR - cropLocal.x)
                // Clamp bottom edge to image height
                const maxBottomBR = imgSnap ? imgSnap.height : selectedLayer.height
                let bottomBR = Math.min(currentY, maxBottomBR)
                let newHBR = Math.max(minSize, bottomBR - cropLocal.y)
                newCrop = {
                    x: cropLocal.x,
                    y: cropLocal.y,
                    w: newWBR,
                    h: newHBR,
                }
                break
            case 'bl': // Bottom-Left
                // Clamp left edge to 0
                let newXBL = Math.max(0, Math.min(currentX, cropLocal.x + cropLocal.w - minSize))
                let newWBL = Math.max(minSize, cropLocal.x + cropLocal.w - newXBL)
                // Clamp bottom edge to image height
                const maxBottomBL = imgSnap ? imgSnap.height : selectedLayer.height
                let bottomBL = Math.min(currentY, maxBottomBL)
                let newHBL = Math.max(minSize, bottomBL - cropLocal.y)
                newCrop = {
                    x: newXBL,
                    y: cropLocal.y,
                    w: newWBL,
                    h: newHBL,
                }
                break
            default:
                return
        }

        // Apply constraints using clampLocal
        const constrainedCrop = clampLocal(newCrop)

        // Update the corner handle position to match the constrained crop
        let handleX = constrainedCrop.x
        let handleY = constrainedCrop.y

        switch (type) {
            case 'tr':
                handleX = constrainedCrop.x + constrainedCrop.w
                break
            case 'br':
                handleX = constrainedCrop.x + constrainedCrop.w
                handleY = constrainedCrop.y + constrainedCrop.h
                break
            case 'bl':
                handleY = constrainedCrop.y + constrainedCrop.h
                break
            default:
                break
        }

        e.target.position({x: handleX, y: handleY})

        // Update crop rectangle
        if (cropRectRef.current) {
            const node = cropRectRef.current
            node.x(constrainedCrop.x)
            node.y(constrainedCrop.y)
            node.width(constrainedCrop.w)
            node.height(constrainedCrop.h)
        }

        setCropLocal(constrainedCrop)
        overlayLayerRef.current?.batchDraw?.()
    }

    const onCropTransform = () => {
        if (!cropRectRef.current) return
        const node = cropRectRef.current
        const newW = node.width() * node.scaleX()
        const newH = node.height() * node.scaleY()

        // Enforce minimum size constraints
        const constrainedW = Math.max(minSize, newW)
        const constrainedH = Math.max(minSize, newH)

        const next = clampLocal({x: node.x(), y: node.y(), w: constrainedW, h: constrainedH})

        // Normalize: write real size into width/height; reset scale to 1
        node.scaleX(1)
        node.scaleY(1)
        node.width(next.w)
        node.height(next.h)
        node.x(next.x)
        node.y(next.y)
        setCropLocal(next)
        overlayLayerRef.current?.batchDraw?.()
    }

    const onCropDragMove = () => {
        if (!cropRectRef.current) return
        const node = cropRectRef.current
        const next = clampLocal({x: node.x(), y: node.y(), w: node.width(), h: node.height()})
        node.x(next.x)
        node.y(next.y)
        setCropLocal((c) => ({...c, x: next.x, y: next.y}))
        overlayLayerRef.current?.batchDraw?.()
    }

    // ---------------------- Preview: rotated crop at natural resolution ----------------------
    const drawRotatedCropToCanvas = (canvas) => {
        if (!imgEl || !imgSnap) return
        const dispW = imgSnap.width
        const dispH = imgSnap.height

        const sxRatio = imgEl.width / dispW
        const syRatio = imgEl.height / dispH

        const sx = Math.round(cropLocal.x * sxRatio)
        const sy = Math.round(cropLocal.y * syRatio)
        const sw = Math.round(cropLocal.w * sxRatio)
        const sh = Math.round(cropLocal.h * syRatio)

        const theta = (imgSnap.rotation * Math.PI) / 180
        const cos = Math.abs(Math.cos(theta))
        const sin = Math.abs(Math.sin(theta))
        const outW = Math.max(1, Math.floor(sw * cos + sh * sin))
        const outH = Math.max(1, Math.floor(sw * sin + sh * cos))

        canvas.width = outW
        canvas.height = outH
        const ctx = canvas.getContext('2d')
        ctx.clearRect(0, 0, outW, outH)
        ctx.imageSmoothingEnabled = true

        ctx.save()
        ctx.translate(outW / 2, outH / 2)
        ctx.rotate(theta)
        ctx.drawImage(imgEl, sx, sy, sw, sh, -sw / 2, -sh / 2, sw, sh)
        ctx.restore()
    }

    // Live preview during crop
    useEffect(() => {
        if (mode !== 'crop') return
        if (!previewCanvasRef.current) return
        drawRotatedCropToCanvas(previewCanvasRef.current)
    }, [mode, cropLocal, imgSnap, imgEl])

    const groundRef = useRef(null)

    useEffect(() => {
        if (groundRef.current) {
            groundRef.current.moveToTop()
        }

        // Reset to crop mode when selectedLayer changes
        if (selectedLayer.src && image) {
            setMode('edit') // Reset first
            setTimeout(() => {
                enterCropMode()
            }, 100) // Small delay to ensure state is updated
        }
    }, [selectedLayer, image])

    useEffect(() => {
        dispatch(actionSaveCropLocal(cropLocal))
    }, [mode, imgSnap, cropLocal, image])

    // Reset crop mode when selectedLayer.src changes
    useEffect(() => {
        if (selectedLayer.src) {
            setMode('edit')
            setImgSnap(null)
            setCropLocal({
                x: selectedLayer.x,
                y: selectedLayer.y,
                w: selectedLayer.width,
                h: selectedLayer.height,
            })

            // Recalculate minimum size when layer changes
            // Note: This will be recalculated properly in enterCropMode when imgSnap is available
            if (selectedLayer.width && selectedLayer.height) {
                // Use a fallback calculation based on layer dimensions
                const dynamicMinSize = calculateMinSize(selectedLayer.width, selectedLayer.height)
                setMinSize(dynamicMinSize)
            }
        }
    }, [selectedLayer.src])

    return (
        <Group
            x={selectedLayer.x}
            y={selectedLayer.y}
            offsetX={selectedLayer.offsetX}
            offsetY={selectedLayer.offsetY}
            ref={groundRef}
            rotation={selectedLayer.rotation}
            scaleX={selectedLayer.scaleX}
            scaleY={selectedLayer.scaleY}
        >
            {/* Image layer */}
            {selectedLayer.src && (
                <KonvaImage
                    ref={imageRef}
                    image={image}
                    x={fitBox.x}
                    y={fitBox.y}
                    width={selectedLayer.width}
                    height={selectedLayer.height}
                />
            )}

            {/* Dimming overlay - only show in crop mode */}
            {mode === 'crop' &&
                imgSnap &&
                cropLocal &&
                (() => {
                    return (
                        <Group x={imgSnap.x} y={imgSnap.y} scaleX={imgSnap.scaleX} scaleY={imgSnap.scaleY}>
                            {/* Top dark overlay */}
                            <Rect
                                x={0}
                                y={0}
                                width={imgSnap.width}
                                height={Math.max(0, cropLocal.y)}
                                fill="rgba(0, 0, 0, 0.24)"
                            />

                            {/* Left dark overlay */}
                            <Rect
                                x={0}
                                y={Math.max(0, cropLocal.y)}
                                width={Math.max(0, cropLocal.x)}
                                height={Math.max(0, cropLocal.h)}
                                fill="rgba(0, 0, 0, 0.24)"
                            />

                            {/* Right dark overlay */}
                            <Rect
                                x={Math.max(0, cropLocal.x + cropLocal.w)}
                                y={Math.max(0, cropLocal.y)}
                                width={Math.max(0, imgSnap.width - (cropLocal.x + cropLocal.w))}
                                height={Math.max(0, cropLocal.h)}
                                fill="rgba(0, 0, 0, 0.24)"
                            />

                            {/* Bottom dark overlay */}
                            <Rect
                                x={0}
                                y={Math.max(0, cropLocal.y + cropLocal.h)}
                                width={imgSnap.width}
                                height={Math.max(0, imgSnap.height - (cropLocal.y + cropLocal.h))}
                                fill="rgba(0, 0, 0, 0.24)"
                            />
                        </Group>
                    )
                })()}

            {/* Overlay layer: crop UI */}
            <Group x={imgSnap?.x} y={imgSnap?.y} scaleX={imgSnap?.scaleX} scaleY={imgSnap?.scaleY}>
                <Rect
                    ref={cropRectRef}
                    x={cropLocal.x}
                    y={cropLocal.y}
                    width={cropLocal.w}
                    height={cropLocal.h}
                    strokeWidth={2}
                    strokeScaleEnabled={false}
                    dash={[6, 4]}
                    draggable
                    onDragMove={onCropDragMove}
                    onTransform={onCropTransform}
                    onTransformEnd={onCropTransform}
                />
                <Transformer
                    anchorStroke="transparent"
                    ref={trCropRef}
                    rotateEnabled={false}
                    flipEnabled={false}
                    borderEnabled={false}
                    enabledAnchors={[]}
                    nodes={cropRectRef.current ? [cropRectRef.current] : []}
                    boundBoxFunc={(oldBox, newBox) => {
                        if (newBox.width < minSize || newBox.height < minSize) {
                            return oldBox
                        }
                        return newBox
                    }}
                />
                {/* Custom L-shaped corner handles */}
                {mode === 'crop' && imgSnap && (
                    <>
                        {/* Top-Left */}
                        <Group
                            x={cropLocal.x}
                            y={cropLocal.y}
                            draggable
                            onDragMove={onCornerDragMove('tl')}
                            onMouseEnter={(e) => (e.target.getStage().container().style.cursor = 'nwse-resize')}
                            onMouseLeave={(e) => (e.target.getStage().container().style.cursor = 'default')}
                        >
                            {/* Horizontal line - only show inside crop area */}
                            <Line
                                points={[
                                    0,
                                    handleSize.strokeWidth / 2,
                                    Math.min(handleSize.handleLen, cropLocal.w),
                                    handleSize.strokeWidth / 2,
                                ]}
                                stroke="#007BFF"
                                strokeWidth={handleSize.strokeWidth}
                            />
                            {/* Vertical line - only show inside crop area */}
                            <Line
                                points={[
                                    handleSize.strokeWidth / 2,
                                    0,
                                    handleSize.strokeWidth / 2,
                                    Math.min(handleSize.handleLen, cropLocal.h),
                                ]}
                                stroke="#007BFF"
                                strokeWidth={handleSize.strokeWidth}
                            />
                            <Rect
                                x={-handleSize.handleHit / 2}
                                y={-handleSize.handleHit / 2}
                                width={handleSize.handleHit}
                                height={handleSize.handleHit}
                                fill="rgba(0,0,0, 0.001)"
                            />
                        </Group>

                        {/* Top-Right */}
                        <Group
                            x={cropLocal.x + cropLocal.w}
                            y={cropLocal.y}
                            draggable
                            onDragMove={onCornerDragMove('tr')}
                            onMouseEnter={(e) => (e.target.getStage().container().style.cursor = 'nesw-resize')}
                            onMouseLeave={(e) => (e.target.getStage().container().style.cursor = 'default')}
                        >
                            {/* Horizontal line - only show inside crop area */}
                            <Line
                                points={[
                                    0,
                                    handleSize.strokeWidth / 2,
                                    -Math.min(handleSize.handleLen, cropLocal.w),
                                    handleSize.strokeWidth / 2,
                                ]}
                                stroke="#007BFF"
                                strokeWidth={handleSize.strokeWidth}
                            />
                            {/* Vertical line - only show inside crop area */}
                            <Line
                                points={[
                                    -handleSize.strokeWidth / 2,
                                    0,
                                    -handleSize.strokeWidth / 2,
                                    Math.min(handleSize.handleLen, cropLocal.h),
                                ]}
                                stroke="#007BFF"
                                strokeWidth={handleSize.strokeWidth}
                            />
                            <Rect
                                x={-handleSize.handleHit / 2}
                                y={-handleSize.handleHit / 2}
                                width={handleSize.handleHit}
                                height={handleSize.handleHit}
                                fill="rgba(0,0,0,0.001)"
                            />
                        </Group>

                        {/* Bottom-Right */}
                        <Group
                            x={cropLocal.x + cropLocal.w}
                            y={cropLocal.y + cropLocal.h}
                            draggable
                            onDragMove={onCornerDragMove('br')}
                            onMouseEnter={(e) => (e.target.getStage().container().style.cursor = 'nwse-resize')}
                            onMouseLeave={(e) => (e.target.getStage().container().style.cursor = 'default')}
                        >
                            {/* Horizontal line - only show inside crop area */}
                            <Line
                                points={[
                                    0,
                                    -handleSize.strokeWidth / 2,
                                    -Math.min(handleSize.handleLen, cropLocal.w),
                                    -handleSize.strokeWidth / 2,
                                ]}
                                stroke="#007BFF"
                                strokeWidth={handleSize.strokeWidth}
                            />
                            {/* Vertical line - only show inside crop area */}
                            <Line
                                points={[
                                    -handleSize.strokeWidth / 2,
                                    0,
                                    -handleSize.strokeWidth / 2,
                                    -Math.min(handleSize.handleLen, cropLocal.h),
                                ]}
                                stroke="#007BFF"
                                strokeWidth={handleSize.strokeWidth}
                            />
                            <Rect
                                x={-handleSize.handleHit / 2}
                                y={-handleSize.handleHit / 2}
                                width={handleSize.handleHit}
                                height={handleSize.handleHit}
                                fill="rgba(0,0,0,0.001)"
                            />
                        </Group>

                        {/* Bottom-Left */}
                        <Group
                            x={cropLocal.x}
                            y={cropLocal.y + cropLocal.h}
                            draggable
                            onDragMove={onCornerDragMove('bl')}
                            onMouseEnter={(e) => (e.target.getStage().container().style.cursor = 'nesw-resize')}
                            onMouseLeave={(e) => (e.target.getStage().container().style.cursor = 'default')}
                        >
                            {/* Horizontal line - only show inside crop area */}
                            <Line
                                points={[
                                    0,
                                    -handleSize.strokeWidth / 2,
                                    Math.min(handleSize.handleLen, cropLocal.w),
                                    -handleSize.strokeWidth / 2,
                                ]}
                                stroke="#007BFF"
                                strokeWidth={handleSize.strokeWidth}
                            />
                            {/* Vertical line - only show inside crop area */}
                            <Line
                                points={[
                                    handleSize.strokeWidth / 2,
                                    0,
                                    handleSize.strokeWidth / 2,
                                    -Math.min(handleSize.handleLen, cropLocal.h),
                                ]}
                                stroke="#007BFF"
                                strokeWidth={handleSize.strokeWidth}
                            />
                            <Rect
                                x={-handleSize.handleHit / 2}
                                y={-handleSize.handleHit / 2}
                                width={handleSize.handleHit}
                                height={handleSize.handleHit}
                                fill="rgba(0,0,0,0.001)"
                            />
                        </Group>
                        {/* Top-Middle (center of top edge) */}
                        <Group
                            x={cropLocal.x + cropLocal.w / 2}
                            y={cropLocal.y}
                            draggable
                            onDragMove={onEdgeDragMove('tm')}
                            onMouseEnter={(e) => (e.target.getStage().container().style.cursor = 'ns-resize')}
                            onMouseLeave={(e) => (e.target.getStage().container().style.cursor = 'default')}
                        >
                            <Line
                                points={[
                                    -Math.min(handleSize.handleLen, cropLocal.w) / 2,
                                    handleSize.strokeWidth / 2,
                                    Math.min(handleSize.handleLen, cropLocal.w) / 2,
                                    handleSize.strokeWidth / 2,
                                ]}
                                stroke="#007BFF"
                                strokeWidth={handleSize.strokeWidth}
                            />
                            <Rect
                                x={-handleSize.handleHit / 2}
                                y={-handleSize.handleHit / 2}
                                width={handleSize.handleHit}
                                height={handleSize.handleHit}
                                fill="rgba(0,0,0,0.001)"
                            />
                        </Group>
                        {/* Right-Middle (center of right edge) */}
                        <Group
                            x={cropLocal.x + cropLocal.w}
                            y={cropLocal.y + cropLocal.h / 2}
                            draggable
                            onDragMove={onEdgeDragMove('rm')}
                            onMouseEnter={(e) => (e.target.getStage().container().style.cursor = 'ew-resize')}
                            onMouseLeave={(e) => (e.target.getStage().container().style.cursor = 'default')}
                        >
                            <Line
                                points={[
                                    -handleSize.strokeWidth / 2,
                                    -Math.min(handleSize.handleLen, cropLocal.h) / 2,
                                    -handleSize.strokeWidth / 2,
                                    Math.min(handleSize.handleLen, cropLocal.h) / 2,
                                ]}
                                stroke="#007BFF"
                                strokeWidth={handleSize.strokeWidth}
                            />
                            <Rect
                                x={-handleSize.handleHit / 2}
                                y={-handleSize.handleHit / 2}
                                width={handleSize.handleHit}
                                height={handleSize.handleHit}
                                fill="rgba(0,0,0,0.001)"
                            />
                        </Group>
                        {/* Bottom-Middle (center of bottom edge) */}
                        <Group
                            x={cropLocal.x + cropLocal.w / 2}
                            y={cropLocal.y + cropLocal.h}
                            draggable
                            onDragMove={onEdgeDragMove('bm')}
                            onMouseEnter={(e) => (e.target.getStage().container().style.cursor = 'ns-resize')}
                            onMouseLeave={(e) => (e.target.getStage().container().style.cursor = 'default')}
                        >
                            <Line
                                points={[
                                    -Math.min(handleSize.handleLen, cropLocal.w) / 2,
                                    -handleSize.strokeWidth / 2,
                                    Math.min(handleSize.handleLen, cropLocal.w) / 2,
                                    -handleSize.strokeWidth / 2,
                                ]}
                                stroke="#007BFF"
                                strokeWidth={handleSize.strokeWidth}
                            />
                            <Rect
                                x={-handleSize.handleHit / 2}
                                y={-handleSize.handleHit / 2}
                                width={handleSize.handleHit}
                                height={handleSize.handleHit}
                                fill="rgba(0,0,0,0.001)"
                            />
                        </Group>
                        {/* Left-Middle (center of left edge) */}
                        <Group
                            x={cropLocal.x}
                            y={cropLocal.y + cropLocal.h / 2}
                            draggable
                            onDragMove={onEdgeDragMove('lm')}
                            onMouseEnter={(e) => (e.target.getStage().container().style.cursor = 'ew-resize')}
                            onMouseLeave={(e) => (e.target.getStage().container().style.cursor = 'default')}
                        >
                            <Line
                                points={[
                                    handleSize.strokeWidth / 2,
                                    -Math.min(handleSize.handleLen, cropLocal.h) / 2,
                                    handleSize.strokeWidth / 2,
                                    Math.min(handleSize.handleLen, cropLocal.h) / 2,
                                ]}
                                stroke="#007BFF"
                                strokeWidth={handleSize.strokeWidth}
                            />
                            <Rect
                                x={-handleSize.handleHit / 2}
                                y={-handleSize.handleHit / 2}
                                width={handleSize.handleHit}
                                height={handleSize.handleHit}
                                fill="rgba(0,0,0,0.001)"
                            />
                        </Group>
                    </>
                )}
            </Group>
        </Group>
    )
}

